import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import LazyImage from '@/components/LazyImage';

const services = [
  {
    id: 'bucatarie',
    title: 'Bucătărie',
    description: 'Bucătării funcționale și elegante, proiectate pe măsura spațiului tău.',
    coverImage: '/pictures/buc1.jpg',
    color: 'bg-orange-100 text-orange-700',
    images: [
      '/pictures/buc1.jpg','/pictures/buc2.jpg','/pictures/buc3.jpg','/pictures/buc4-main.jpg',
      '/pictures/buc5.jpg','/pictures/buc6.jpg','/pictures/buc7.jpg','/pictures/buc8.jpg',
      '/pictures/buc9.jpg','/pictures/buc10.jpg','/pictures/buc11.jpg','/pictures/buc12.jpg',
      '/pictures/buc13.jpg','/pictures/buc14.jpg','/pictures/buc15.jpg','/pictures/buc16.jpg',
      '/pictures/buc17.jpg','/pictures/buc18.jpg','/pictures/buc20.jpg','/pictures/buc21.jpg',
      '/pictures/buc22.jpg','/pictures/buc23.jpg','/pictures/buc24.jpg','/pictures/buc25.jpg',
      '/pictures/buc26.jpg','/pictures/buc27.jpg',
    ],
  },
  {
    id: 'living',
    title: 'Living & Dining',
    description: 'Spații de zi care impresionează — biblioteci, unități TV și dining personalizat.',
    coverImage: '/pictures/living12.jpg',
    color: 'bg-blue-100 text-blue-700',
    images: [
      '/pictures/living12.jpg','/pictures/living29.jpeg','/pictures/living1.jpg','/pictures/living2.jpg',
      '/pictures/living3.jpg','/pictures/living4.jpg','/pictures/living5.jpg',
      '/pictures/living6.jpg','/pictures/living7.jpg','/pictures/living8.jpg',
      '/pictures/living9.jpg','/pictures/living10.jpg','/pictures/living11.jpg',
      '/pictures/living13.jpg','/pictures/living14.jpg','/pictures/living15.jpg',
      '/pictures/living16.jpg',
    ],
  },
  {
    id: 'dormitor',
    title: 'Dormitor',
    description: 'Dormitoare confortabile cu paturi și noptiere realizate pe comandă.',
    coverImage: '/pictures/dormitor15-main.jpeg',
    color: 'bg-purple-100 text-purple-700',
    images: [
        '/pictures/dormitor13-main.jpeg', '/pictures/dormitor14.jpeg', '/pictures/dormitor15-main.jpeg',  '/pictures/dormitor16.jpeg',
      '/pictures/dormitor10-main.jpg','/pictures/dormitor1.jpg','/pictures/dormitor2.jpg',
      '/pictures/dormitor3.jpg','/pictures/dormitor4.jpg','/pictures/dormitor5.jpg',
      '/pictures/dormitor6.jpg','/pictures/dormitor7-main.jpg','/pictures/dormitor8.jpg',
      '/pictures/dormitor9.jpg','/pictures/dormitor11.jpg',
    ],
  },
  {
    id: 'dulap',
    title: 'Dulap & Dressing',
    description: 'Dulapuri și dressinguri walk-in personalizate, cu sisteme moderne de organizare.',
    coverImage: '/pictures/dulap1.jpg',
    color: 'bg-amber-100 text-amber-700',
    images: [
      '/pictures/dulap1.jpg','/pictures/dulap20.jpg','/pictures/dulap2.jpg','/pictures/dulap3.jpg','/pictures/dulap4.jpg',
      '/pictures/dulap5.jpg','/pictures/dulap6.jpg', '/pictures/dulap17.jpg','/pictures/dulap7.jpg','/pictures/dulap8.jpg',
      '/pictures/dulap9.jpg','/pictures/dulap10.jpg','/pictures/dulap11.jpg','/pictures/dulap12.jpg',
      '/pictures/dulap13.jpg','/pictures/dulap14.jpg','/pictures/dulap15.jpg','/pictures/dulap16.jpg',
      '/pictures/sifonier.jpg','/pictures/sifonier2.jpg','/pictures/sifonier3.jpg','/pictures/sifonier4.jpg',
    ],
  },
  {
    id: 'birou',
    title: 'Birou',
    description: 'Spații de lucru productive și elegante — birouri și sisteme de organizare.',
    coverImage: '/pictures/birou.jpg',
    color: 'bg-green-100 text-green-700',
    images: [
      '/pictures/birou.jpg','/pictures/birou1.jpg','/pictures/birou2.jpg',
    ],
  },
  {
    id: 'baie',
    title: 'Baie',
    description: 'Mobilier de baie elegant și rezistent, adaptat oricărui stil de interior.',
    coverImage: '/pictures/baie1.jpg',
    color: 'bg-cyan-100 text-cyan-700',
    images: [
      '/pictures/baie1.jpg','/pictures/baie2.jpg','/pictures/baie3.jpg',
      '/pictures/baie4.jpg','/pictures/baie5.jpg',
    ],
  }
];

/* ── Lightbox ── */
function Lightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
      >
        <X size={20} />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
        {idx + 1} / {images.length}
      </div>

      {/* Image */}
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.2 }}
          className="max-h-[85vh] max-w-[90vw] rounded-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <LazyImage
            src={images[idx]}
            alt={`foto ${idx + 1}`}
            className="max-h-[85vh] max-w-[90vw] object-contain"
          />
        </motion.div>
      </AnimatePresence>

      {/* Prev / Next */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}
    </motion.div>
  );
}

/* ── Service modal (photo grid) ── */
function ServiceModal({ service, onClose }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="relative w-full sm:max-w-5xl bg-white dark:bg-[#242322] rounded-t-2xl sm:rounded-2xl overflow-hidden max-h-[92vh] sm:max-h-[88vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 sm:px-8 py-4 border-b border-[#E3DACB] dark:border-[#3D3A37] flex-shrink-0">
            <div>
              <h3 className="font-['Outfit'] text-xl sm:text-2xl font-medium text-[#2C2A29] dark:text-[#F0EDE8]">
                {service.title}
              </h3>
              <p className="text-sm text-[#6B6561] dark:text-[#9B9590] mt-0.5">{service.description}</p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex-shrink-0 rounded-full bg-[#F9F6F0] dark:bg-[#2E2C2A] text-[#6B6561] dark:text-[#9B9590] flex items-center justify-center hover:bg-[#E3DACB] dark:hover:bg-[#3D3A37] transition-colors ml-4"
            >
              <X size={16} />
            </button>
          </div>

          {/* Photo grid — scrollable */}
          <div className="overflow-y-auto p-4 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              {service.images.map((src, i) => (
                <button
                  key={src}
                  onClick={() => setLightboxIdx(i)}
                  className="relative aspect-square rounded-xl overflow-hidden group focus:outline-none"
                >
                  <LazyImage
                    src={src}
                    alt={`${service.title} ${i + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-xl pointer-events-none" />
                </button>
              ))}
            </div>

            {/* CTA */}
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                setTimeout(() => {
                  const el = document.querySelector('#contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }, 300);
              }}
              className="flex items-center justify-center gap-2 w-full mt-5 py-3.5 bg-[#A67B5B] text-white font-medium rounded-xl hover:bg-[#8B654A] transition-colors duration-300"
            >
              Cere ofertă pentru {service.title.toLowerCase()}
            </a>
          </div>
        </motion.div>
      </motion.div>

      {/* Lightbox on top of modal */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <Lightbox
            images={service.images}
            startIndex={lightboxIdx}
            onClose={() => setLightboxIdx(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/* ── Service card ── */
function ServiceCard({ service, index, onClick }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.07 }}
      onClick={onClick}
      className="group relative cursor-pointer rounded-2xl overflow-hidden aspect-[4/3] sm:aspect-[3/2] active:scale-[0.98] transition-transform"
    >
      <img
        src={service.coverImage}
        alt={service.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

      {/* Photo count badge */}
      <div className="absolute top-3 right-3">
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${service.color}`}>
          {service.images.length} foto
        </span>
      </div>

      {/* Bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
        {/* Mobile tap hint */}
        <div className="sm:hidden text-white/50 text-[10px] mb-1">Atinge pentru a vedea</div>
        <h3 className="font-['Outfit'] text-lg sm:text-xl font-semibold text-white mb-1">
          {service.title}
        </h3>
        <p className="text-xs sm:text-sm text-white/70 leading-snug line-clamp-2 mb-3">
          {service.description}
        </p>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#A67B5B] px-3 py-1.5 rounded-full group-hover:bg-[#8B654A] transition-colors">
          Vezi poze
          <ChevronDown size={12} className="rotate-[-90deg]" />
        </span>
      </div>

      <div className="absolute inset-0 rounded-2xl ring-0 group-hover:ring-2 group-hover:ring-[#A67B5B]/60 transition-all duration-300 pointer-events-none" />
    </motion.div>
  );
}

/* ── Main section ── */
export default function PortfolioSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.body.style.overflow = selected ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selected]);

  return (
    <section id="portofoliu" data-testid="portfolio-section" className="py-20 lg:py-32 bg-[#F9F6F0] dark:bg-[#1A1918]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#A67B5B] mb-4">
            Portofoliu
          </p>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-[#2C2A29] dark:text-[#F0EDE8]">
            Lucrările noastre, pe servicii
          </h2>
          <p className="mt-4 text-base text-[#6B6561] dark:text-[#9B9590] max-w-xl mx-auto leading-relaxed">
            Alege categoria care te interesează și explorează fotografiile lucrărilor realizate.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, i) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={i}
              onClick={() => setSelected(service)}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-12"
        >
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector('#contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#A67B5B] text-white font-medium rounded-full hover:bg-[#8B654A] transition-colors duration-300 shadow-md hover:shadow-lg"
          >
            Cere ofertă pentru proiectul tău
          </a>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && <ServiceModal service={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
